import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import MobileNav from '../../components/feature/MobileNav';

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  total_amount: number;
  status: string;
  created_at: string;
  items: any[];
}

export default function TrackOrder() {
  const { t } = useTranslation();
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrackOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setOrder(null);

    try {
      const { data, error: supabaseError } = await supabase
        .from('orders')
        .select('*')
        .eq('order_number', orderNumber.trim())
        .eq('customer_email', email.trim().toLowerCase())
        .single();

      if (supabaseError || !data) {
        setError(t('trackOrder.notFound'));
        setLoading(false);
        return;
      }

      setOrder(data);
    } catch (err) {
      setError(t('trackOrder.error'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'ri-time-line';
      case 'processing':
        return 'ri-loader-4-line';
      case 'completed':
        return 'ri-checkbox-circle-line';
      case 'cancelled':
        return 'ri-close-circle-line';
      default:
        return 'ri-information-line';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-teal-600 text-white px-6 pt-8 pb-8">
        <h1 className="text-2xl font-bold">{t('trackOrder.title')}</h1>
        <p className="text-teal-100 text-sm mt-2">{t('trackOrder.subtitle')}</p>
      </div>

      {/* Track Order Form */}
      <div className="px-6 -mt-4">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <form onSubmit={handleTrackOrder} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('trackOrder.orderNumber')}
              </label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder={t('trackOrder.orderNumberPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('trackOrder.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('trackOrder.emailPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                <i className="ri-error-warning-line text-lg"></i>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 text-white py-3 rounded-xl font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  {t('trackOrder.searching')}
                </>
              ) : (
                <>
                  <i className="ri-search-line mr-2"></i>
                  {t('trackOrder.trackButton')}
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Order Details */}
      {order && (
        <div className="px-6 mt-6 space-y-4">
          {/* Status Card */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">{t('trackOrder.orderStatus')}</h2>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(order.status)}`}>
                <i className={`${getStatusIcon(order.status)} mr-1`}></i>
                {order.status}
              </span>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('trackOrder.orderNumber')}:</span>
                <span className="font-semibold text-gray-900">{order.order_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('trackOrder.orderDate')}:</span>
                <span className="font-semibold text-gray-900">
                  {new Date(order.created_at).toLocaleDateString('sv-SE')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('trackOrder.totalAmount')}:</span>
                <span className="font-semibold text-gray-900">{order.total_amount} kr</span>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('trackOrder.customerInfo')}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <div className="text-gray-600 text-xs">{t('trackOrder.name')}</div>
                  <div className="font-semibold text-gray-900">{order.customer_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center">
                  <i className="ri-mail-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <div className="text-gray-600 text-xs">{t('trackOrder.email')}</div>
                  <div className="font-semibold text-gray-900">{order.customer_email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center">
                  <i className="ri-phone-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <div className="text-gray-600 text-xs">{t('trackOrder.phone')}</div>
                  <div className="font-semibold text-gray-900">{order.customer_phone}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Items */}
          {order.items && order.items.length > 0 && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('trackOrder.orderItems')}</h3>
              <div className="space-y-3">
                {order.items.map((item: any, index: number) => (
                  <div key={index} className="flex items-center gap-4 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 text-sm">{item.name}</h4>
                      {item.size && (
                        <p className="text-xs text-gray-600 mt-1">
                          {t('trackOrder.size')}: {item.size}
                        </p>
                      )}
                      <p className="text-xs text-gray-600 mt-1">
                        {t('trackOrder.quantity')}: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{item.price} kr</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <MobileNav />
    </div>
  );
}